import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;

/**
 * Iter-56.1 + Iter-56.2 + Iter-56.3 + Iter-60.1 — Sandboxed custom-tracking runtime.
 *
 * SECURITY CONTRACT (Iter-56.1, unchanged)
 * =======================================
 * Approved third-party tracking snippets NEVER run in the first-party
 * landing-page DOM. Each snippet gets its own hidden `<iframe>` with:
 *
 *     sandbox="allow-scripts"        (NO allow-same-origin)
 *     srcdoc="<the approved html/js>"
 *
 * The frame boots at a null / opaque origin. Same-origin-policy denies
 * the snippet from reaching the parent document, cookies, storage, form
 * inputs, or React state. Parent → sandbox comms are one-way postMessage
 * carrying only the fixed PII-safe `SAFE_KEYS` allow-list.
 *
 * LIVE KILL SWITCH + DISABLE (Iter-56.2)
 * ======================================
 * Every REFRESH_MS the loader re-hits `/public/custom-tracking/{slug}`
 * so admin actions take effect on already-open tabs without requiring
 * the visitor to reload:
 *
 *   - kill_switch flips ON  → every sandbox iframe on the page is
 *                              removed, event fan-out shuts down.
 *   - snippet is disabled    → its iframe is removed; other snippets
 *                              (still approved+enabled) keep firing.
 *   - snippet is rejected    → same as disable.
 *
 * Polling is skipped entirely on preview / demo / sample surfaces (no
 * network fingerprint on ops walkthroughs).
 *
 * ITER-56.3 CORRECTNESS CLEANUP
 * =============================
 *   1. EXACT trigger matching. A snippet configured for `thank_you_page`
 *      receives only `thank_you_page`. A snippet configured for
 *      `form_submit_success` receives only `form_submit_success`. The
 *      previous compatibility cross-map between the two internal TY
 *      events is REMOVED because it caused double-firing of conversions.
 *   2. INITIAL-SNAPSHOT session gate. The IDs of approved+enabled
 *      snippets returned by the FIRST successful fetch are the only IDs
 *      eligible to execute during that page session. Subsequent polls
 *      may remove snippets (disable / reject / kill / no-longer-in-list)
 *      but may NEVER add a snippet whose ID was not in the initial
 *      snapshot. Newly approved snippets take effect on the NEXT PAGE
 *      LOAD only.
 *   3. STICKY per-session kill switch. Once a page session has observed
 *      `kill_switch: true` it stays custom-tracking-disabled until the
 *      visitor reloads — even if an admin flips the switch back OFF
 *      during the same session. Emergency shutdowns stay shut for
 *      already-open consumer sessions.
 *
 * ITER-60.1 CONVERSION-SAFETY HARDENING
 * =====================================
 *   4. EXACT-ONLY matcher. The old
 *      matcher-with-OR-clause treated any snippet whose trigger was
 *      `custom_event` as a wildcard that received EVERY emitted
 *      event — that contradicts exact-trigger behaviour and is a
 *      source of duplicate conversions when a third-party vendor
 *      uses the `custom_event` trigger for their own signal. The
 *      matcher is now:
 *
 *              const matches = t === event;
 *
 *      A `custom_event` snippet receives `custom_event` and nothing
 *      else. If a future all-events trigger is wanted it must be an
 *      explicit new trigger (`all_events`) — not overloaded onto
 *      `custom_event`.
 *   5. AUTOMATIC event idempotency. `page_view`, `thank_you_page`, and
 *      `form_submit_success` are automatic surface-initialisation
 *      events. Each such event is delivered at most ONCE per
 *      (snippet_id, event) per page session, guarded by
 *      `firedAutoEventsRef`. Explicit `lf-tracking-emit` dispatches
 *      (`form_start`, `custom_event`, or any other application-driven
 *      signal) are NOT touched by this guard — they still fire every
 *      time the app dispatches them, which is the desired contract.
 *   6. POLLING NEVER RE-FIRES AUTO EVENTS. The 30 s poll updates the
 *      loader's internal safety state (`snippets`, `allowedContext`)
 *      but the auto-emit path is now bound to the iframe-mount
 *      reconciliation, not to a `useEffect` whose deps include those
 *      values. An unchanged poll observes zero new iframes ⇒ zero
 *      auto events. An identity-only churn in `snippets` (React
 *      creating a new array on every poll) is also caught upstream:
 *      `setSnippets` is only called with a genuinely new shape.
 *   7. PARENT RE-RENDER SAFETY. `context` and `allowedContext` live in
 *      refs. The parent LP / TY page can pass a new `{...}` object on
 *      every render — that no longer re-runs the auto-emit path; the
 *      next event delivered will just see the latest safe context.
 *   8. IFRAME PRESERVATION. Reconcile still keys off snippet ids, so a
 *      poll that returns an equivalent snapshot never tears an iframe
 *      down and recreates it.
 *
 * NAMES
 * =====
 *   - Bridge event: `lf-tracking-emit`
 *   - Iframe postMessage payload:
 *       { type: "lf-tracking-event", event, context, id }
 */

const SAFE_KEYS = [
  "landing_page_slug", "landing_page_id", "form_experience",
  "experiment_variant", "sub_id", "click_id",
  "utm_source", "utm_campaign", "utm_medium",
  "session_id", "event",
];

// Iter-56.2: 30 s cadence. Not analytics — a lightweight safety-state
// refresh so kill-switch / disable actions terminate live sandboxes
// without requiring the visitor to reload the page.
const REFRESH_MS = 30_000;

// "All events" snippets load on landing AND thank-you pages and hear every
// moment: page_view + form_start on the landing page, lead_submit +
// thank_you on the thank-you page (each automatic one once per page load).
export const ALL_EVENTS = "all_events";
export const AUTO_EVENTS = { landing: ["page_view"], thank_you: ["thank_you_page", "form_submit_success"] };
export const ALL_EVENTS_AUTO = { landing: ["page_view"], thank_you: ["lead_submit", "thank_you"] };

// These events only happen on the thank-you page, so snippets listening for them load there too.
const THANK_YOU_TRIGGERS = [ALL_EVENTS, "thank_you_page", "form_submit_success"];

export function snippetMounts(snip, surface) {
  const placement = (snip.placement || "").toLowerCase();
  if (surface === "thank_you") return placement === "thank_you_only" || THANK_YOU_TRIGGERS.includes((snip.trigger || "").toLowerCase());
  return placement !== "thank_you_only";
}

// The landing page's tracking context (sub_id, session_id, utm_*) is kept
// for this tab so the thank-you page can hand the same values to tracking code.
const storeKey = (slug) => `lf_ct_ctx_${slug}`;
function withoutEmpty(ctx) {
  const out = {};
  for (const [k, v] of Object.entries(ctx || {})) if (v !== undefined && v !== null && v !== "") out[k] = v;
  return out;
}
function readStoredContext(slug) {
  try { return JSON.parse(window.sessionStorage.getItem(storeKey(slug)) || "{}") || {}; } catch (_e) { return {}; }
}

// Messages wait until the sandbox has loaded; one posted earlier would land
// in the iframe's throwaway blank document and be lost.
function sendTo(rec, msg) {
  if (!rec.ready) { rec.queue.push(msg); return; }
  rec.awaiting[msg.event] = (rec.awaiting[msg.event] || 0) + 1;
  try { rec.iframe.contentWindow?.postMessage(msg, "*"); } catch (_e) { /* iframe torn down */ }
}


function safeContext(ctx, allowed) {
  const out = {};
  const list = (allowed && allowed.length ? allowed : SAFE_KEYS);
  for (const k of list) {
    if (ctx[k] !== undefined && ctx[k] !== null) out[k] = ctx[k];
  }
  return out;
}


/**
 * Iter-60.1: an incoming poll payload matches the current snippet set
 * iff the two lists agree on id + trigger + placement + code_snippet
 * for every element in identical order. When true we intentionally
 * skip the `setSnippets` call so the iframe-reconcile effect does not
 * re-run and no automatic events are re-fired. `snippets` in the
 * loader is always stored in the server's response order.
 */
function sameSnippetShape(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i], y = b[i];
    if (!x || !y) return false;
    if (x.id !== y.id) return false;
    if ((x.trigger || "") !== (y.trigger || "")) return false;
    if ((x.placement || "") !== (y.placement || "")) return false;
    if ((x.code_snippet || "") !== (y.code_snippet || "")) return false;
  }
  return true;
}


function sameAllowedContext(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}


export default function CustomTrackingLoader({
  slug,
  isPreviewSurface = false,
  isSample = false,
  surface = "landing",             // "landing" | "thank_you"
  context = {},
  fetchUrl = null,                 // affiliate pixels use their own runtime endpoint
}) {
  const containerRef = useRef(null);
  const iframesRef = useRef([]);       // { id, iframe, trigger, placement }
  const killSwitchRef = useRef(false);
  // Iter-56.3: once TRUE for this page session, stays TRUE until reload.
  const killedForSessionRef = useRef(false);
  // Iter-56.3: `null` until the FIRST successful (non-killed) fetch.
  // After that it holds a Set of snippet ids captured from that
  // initial response. Only IDs in this set may execute during the
  // session. Subsequent polls can REMOVE but never ADD.
  const initialSnippetIdsRef = useRef(null);
  const pollTimerRef = useRef(null);

  // Iter-60.1 conversion-safety refs.
  //
  // `contextRef` holds the latest parent-provided context object so
  // the event bridge never has to list `context` in its deps — a
  // parent re-render that passes a fresh `{...}` never re-fires an
  // automatic page_view / conversion.
  //
  // `allowedContextRef` is the same idea for the SAFE_KEYS allow-list
  // the server returns.
  //
  // `firedAutoEventsRef` records "{snippet_id}:{event}" for every
  // AUTOMATIC surface-init event that has already been delivered
  // during this page session. A poll that adds a mid-session snippet
  // is already rejected by the initial-snapshot gate, but even if a
  // re-mount ever were to happen the guard prevents duplicate
  // conversions.
  const contextRef = useRef(context);
  const allowedContextRef = useRef(SAFE_KEYS);
  const firedAutoEventsRef = useRef(new Set());

  const [snippets, setSnippets] = useState([]);
  const [killed, setKilled] = useState(false);

  // Keep the refs synced with the latest parent props. This effect is
  // intentionally cheap and does NOT own event emission.
  const storedJsonRef = useRef("");
  useEffect(() => {
    if (surface === "thank_you") {
      contextRef.current = { ...readStoredContext(slug), ...withoutEmpty(context) };
      return;
    }
    contextRef.current = context || {};
    if (!slug || isPreviewSurface || isSample) return;
    const keep = withoutEmpty(safeContext(context || {}, SAFE_KEYS));
    delete keep.event;
    const json = JSON.stringify(keep);
    if (json === storedJsonRef.current) return;
    storedJsonRef.current = json;
    try { window.sessionStorage.setItem(storeKey(slug), json); } catch (_e) { /* storage blocked */ }
  }, [context, surface, slug, isPreviewSurface, isSample]);

  // -----------------------------------------------------------------
  // 1. Initial fetch + 30 s poll. The poll only reacts to two admin
  //    actions:
  //      * global kill switch flipped ON  → tear down everything.
  //      * an individual snippet is no longer approved+enabled
  //        (disabled by admin OR by the owning MB, rejected, etc.)
  //        → tear down JUST that iframe. Others keep firing.
  //    Never runs on preview / demo / sample.
  //
  //    Iter-56.3:
  //      * initial-snapshot session gate — snippets whose id appears
  //        for the first time AFTER the first fetch are ignored until
  //        reload.
  //      * sticky kill switch — once observed ON in a session, we
  //        remain custom-tracking-disabled even if the server later
  //        flips it back OFF.
  //
  //    Iter-60.1:
  //      * `setSnippets` and `allowedContextRef` writes are guarded
  //        by shape-equality helpers, so a poll that returns the same
  //        payload causes ZERO downstream React work. The reconcile
  //        effect does not re-run and no automatic events fire.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!slug || isPreviewSurface || isSample) {
      setSnippets([]);
      setKilled(false);
      return;
    }
    let cancelled = false;

    const teardownAll = () => {
      for (const rec of iframesRef.current) {
        try { rec.iframe.remove(); } catch (_e) { /* noop */ }
      }
      iframesRef.current = [];
    };

    const applyServerState = (payload) => {
      // Sticky kill: once a session has been killed, ignore every
      // subsequent server response (including a kill=false rebound).
      if (killedForSessionRef.current) {
        return;
      }
      const nextKill = Boolean(payload?.kill_switch);
      if (nextKill) {
        killSwitchRef.current = true;
        killedForSessionRef.current = true;
        setKilled(true);
        teardownAll();
        setSnippets([]);
        return;
      }
      const raw = Array.isArray(payload?.snippets) ? payload.snippets : [];
      const nextAllowed = payload?.allowed_context || SAFE_KEYS;
      // Iter-60.1: allowed_context lives in a ref; do not put it in
      // React state or the reconcile/emit effects would re-run when
      // the server returns a fresh array on every poll.
      if (!sameAllowedContext(allowedContextRef.current, nextAllowed)) {
        allowedContextRef.current = nextAllowed;
      }
      // First successful non-killed fetch defines the session's
      // eligibility set. Snapshot is immutable for the rest of the
      // session — mid-session approvals never mount.
      if (initialSnippetIdsRef.current === null) {
        initialSnippetIdsRef.current = new Set(raw.map((s) => s.id));
        setSnippets(raw);
        return;
      }
      // Subsequent polls: filter to the session snapshot. This is the
      // core "safety > dynamic enabling" contract.
      const filtered = raw.filter((s) => initialSnippetIdsRef.current.has(s.id));
      // Iter-60.1: identity churn guard. If the resulting shape is
      // byte-for-byte the same as the state we already hold, skip the
      // setState call entirely. Otherwise React would produce a new
      // reference on every poll, our reconcile effect would re-run,
      // and — although the guard inside that effect prevents
      // re-mount / re-emit — we would still burn CPU on every tab
      // every 30 s for nothing.
      setSnippets((prev) => (sameSnippetShape(prev, filtered) ? prev : filtered));
    };

    const refresh = () => {
      axios.get(fetchUrl || `${API}/public/custom-tracking/${encodeURIComponent(slug)}`)
        .then((r) => { if (!cancelled) applyServerState(r.data || {}); })
        .catch(() => { /* transient network — keep current state */ });
    };

    refresh();
    pollTimerRef.current = setInterval(refresh, REFRESH_MS);

    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [slug, isPreviewSurface, isSample, fetchUrl]);

  // -----------------------------------------------------------------
  // 2. Reconcile the iframe set against the latest snippet list AND
  //    fire the automatic surface-init events for the freshly-mounted
  //    iframes only.
  //
  //    - Existing iframe whose snippet id is no longer in the list → remove.
  //    - New snippet id → only mount if it belongs to the initial
  //      session snapshot (belt-and-braces vs. the filter in
  //      applyServerState).
  //    - Placement filter: thank_you_only never mounts on the LP;
  //      non-thank_you never mounts on TY.
  //
  //    Iter-60.1: the automatic-emit path is inside this effect and
  //    triggered per NEWLY-MOUNTED iframe. `firedAutoEventsRef` is
  //    the belt guard — even in the pathological case where the same
  //    iframe were somehow reconstructed, its
  //    `${id}:${event}` key would already be present and the event
  //    would not fire. `context` and `allowedContext` come from refs,
  //    so this effect does NOT depend on them and parent rerenders
  //    cannot make it re-run.
  // -----------------------------------------------------------------
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // If killed, we already wiped in applyServerState. Nothing to do.
    if (killed) return;

    const wantedIds = new Set(snippets.map((s) => s.id));
    // Remove iframes whose snippet is no longer approved+enabled.
    iframesRef.current = iframesRef.current.filter((rec) => {
      if (wantedIds.has(rec.id)) return true;
      try { rec.iframe.remove(); } catch (_e) { /* noop */ }
      return false;
    });

    const initialIds = initialSnippetIdsRef.current;
    const currentIds = new Set(iframesRef.current.map((r) => r.id));
    const newlyMounted = [];
    for (const snip of snippets) {
      if (currentIds.has(snip.id)) continue;
      // Iter-56.3: extra belt — never mount an id that was not in
      // the initial snapshot for this session.
      if (initialIds && !initialIds.has(snip.id)) continue;
      const placement = (snip.placement || "").toLowerCase();
      if (!snippetMounts(snip, surface)) continue;

      const iframe = document.createElement("iframe");
      iframe.setAttribute("sandbox", "allow-scripts");
      iframe.setAttribute("aria-hidden", "true");
      iframe.setAttribute("title", "custom-tracking-sandbox");
      iframe.setAttribute("data-lf-tracking-id", snip.id);
      iframe.style.cssText = "position:absolute;width:0;height:0;border:0;visibility:hidden;pointer-events:none";
      // Per-iframe secret so the snippet itself can't fake its health reports.
      const nonce = `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      iframe.srcdoc = buildSandboxSrcdoc(snip, safeContext(contextRef.current, allowedContextRef.current), { nonce });

      const rec = {
        id: snip.id, iframe, trigger: (snip.trigger || "").toLowerCase(),
        placement, ready: false, queue: [], awaiting: {}, nonce,
      };
      iframe.addEventListener("load", () => {
        rec.ready = true;
        const pending = rec.queue;
        rec.queue = [];
        if (killSwitchRef.current) return;
        for (const msg of pending) sendTo(rec, msg);
      }, { once: true });
      container.appendChild(iframe);
      iframesRef.current.push(rec);
      newlyMounted.push(rec);
    }

    // Iter-60.1: per-iframe automatic emission. Fires exactly once
    // per (snippet_id, event) across the whole page session. The
    // guard is the ref, not the effect's dep array, so re-runs of
    // this effect for any reason are safe.
    if (!newlyMounted.length) return;
    if (killSwitchRef.current) return;

    const autoEvents = AUTO_EVENTS[surface] || [];
    const deliver = (rec, evt) => {
      const key = `${rec.id}:${evt}`;
      if (firedAutoEventsRef.current.has(key)) return;
      firedAutoEventsRef.current.add(key);
      sendTo(rec, {
        type: "lf-tracking-event",
        event: evt,
        id: rec.id,
        context: safeContext(
          { ...contextRef.current, event: evt },
          allowedContextRef.current,
        ),
      });
    };
    for (const evt of autoEvents) {
      for (const rec of newlyMounted) {
        // EXACT trigger match — no wildcard on custom_event.
        const matches = rec.trigger === evt;
        if (matches) deliver(rec, evt);
      }
    }
    for (const rec of newlyMounted) {
      if (rec.trigger === ALL_EVENTS) (ALL_EVENTS_AUTO[surface] || []).forEach((evt) => deliver(rec, evt));
    }
  }, [snippets, killed, surface]);

  // Live health: the sandbox says how many listeners ran for each event we
  // sent; only events we actually sent are counted (counts, no visitor data).
  useEffect(() => {
    if (!slug || isPreviewSurface || isSample || fetchUrl) return undefined;
    const onHeard = (ev) => {
      const d = ev?.data;
      if (!d || d.type !== "lf-tracking-heard" || killSwitchRef.current) return;
      const rec = iframesRef.current.find((r) => r.iframe.contentWindow === ev.source);
      if (!rec || d.nonce !== rec.nonce || !rec.awaiting[d.event]) return;
      rec.awaiting[d.event] -= 1;
      axios.post(`${API}/public/custom-tracking/${encodeURIComponent(slug)}/heard`, {
        tracking_id: rec.id,
        event: d.event,
        listeners: Math.max(0, Math.min(1000, Number(d.listeners) || 0)),
        errors: Math.max(0, Math.min(1000, Number(d.errors) || 0)),
      }).catch(() => { /* health is best-effort */ });
    };
    window.addEventListener("message", onHeard);
    return () => window.removeEventListener("message", onHeard);
  }, [slug, isPreviewSurface, isSample, fetchUrl]);

  // -----------------------------------------------------------------
  // 3. Full unmount teardown. Called on route change out of the LP.
  // -----------------------------------------------------------------
  useEffect(() => {
    return () => {
      for (const rec of iframesRef.current) {
        try { rec.iframe.remove(); } catch (_e) { /* noop */ }
      }
      iframesRef.current = [];
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  // -----------------------------------------------------------------
  // 4. Event bridge — the parent app dispatches
  //    window.dispatchEvent(new CustomEvent("lf-tracking-emit", {
  //      detail: { event, context } }))
  //    to fan out an EXPLICIT event to every sandboxed iframe whose
  //    configured trigger equals the emitted event name.
  //
  //    Iter-60.1: registered ONCE per page session (deps = [killed]),
  //    reading the latest snippets / context / allowedContext from
  //    refs. Explicit events are NOT gated by `firedAutoEventsRef` —
  //    legitimate application-level events (`form_start`, vendor-
  //    defined `custom_event`, …) must be free to fire as often as
  //    the application dispatches them. The auto-idempotency guard
  //    applies ONLY to the surface-init events emitted in effect 2.
  //
  //    Iter-56.2: once the kill switch has flipped ON we drop every
  //    outgoing postMessage on the floor so no event reaches an
  //    already-torn-down or in-flight iframe.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (killed) return;

    const onEmit = (ev) => {
      if (killSwitchRef.current) return;
      const detail = ev?.detail || {};
      if (!detail.event) return;
      const event = detail.event;
      const payload = {
        type: "lf-tracking-event",
        event,
        context: safeContext(
          { ...contextRef.current, ...(detail.context || {}), event },
          allowedContextRef.current,
        ),
      };
      for (const rec of iframesRef.current) {
        // Iter-60.1: EXACT trigger match. `custom_event` is a trigger,
        // NOT a wildcard. Only an explicit "All events" snippet hears every event.
        const matches = rec.trigger === event;
        if (!matches && rec.trigger !== ALL_EVENTS) continue;
        sendTo(rec, { ...payload, id: rec.id });
      }
    };
    window.addEventListener("lf-tracking-emit", onEmit);
    return () => window.removeEventListener("lf-tracking-emit", onEmit);
  }, [killed]);

  return (
    <div
      ref={containerRef}
      data-testid="custom-tracking-container"
      data-lf-kill-switch={killed ? "1" : "0"}
      style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
      aria-hidden="true"
    />
  );
}


// ---------------------------------------------------------------------------
// srcdoc builder (Iter-56.1, unchanged)
// ---------------------------------------------------------------------------
// `CustomTracking.context` is readable as soon as the code loads (sub_id,
// session_id, utm_*, page). `CustomTracking.on(name, cb)` hears events;
// thank_you_page/thank_you and form_submit_success/lead_submit are the same
// moments under both names. Test mode (the "Test my code" tool) reports what
// the code heard and catches the calls it tries to make instead of sending them.
export function buildSandboxSrcdoc(snip, context = {}, { test = false, nonce = "" } = {}) {
  const wrapper = `
<!doctype html><html><head><meta charset="utf-8">
<script>
(function () {
  var TEST = __TEST__;
  var NONCE = __NONCE__;
  var P = window.parent;
  var ctx = __CONTEXT__;
  var listeners = { "*": [] };
  var ALIAS = { thank_you_page: "thank_you", thank_you: "thank_you_page", form_submit_success: "lead_submit", lead_submit: "form_submit_success" };
  function report(msg) {
    if (!TEST) return;
    try { msg.type = "lf-tracking-test"; P.postMessage(msg, "*"); } catch (e) {}
  }
  function run(name, c, errs) {
    var n = 0;
    (listeners[name] || []).forEach(function (cb) {
      n++;
      try { cb(c); } catch (e) { errs.push(String((e && e.message) || e)); }
    });
    return n;
  }
  var CT = {
    on: function (event, cb) {
      if (!listeners[event]) listeners[event] = [];
      listeners[event].push(cb);
      report({ kind: "listen", event: event });
    },
    emit: function (event, extra) {
      var c = Object.assign({}, ctx, extra || {}, { event: event });
      var errs = [];
      run(event, c, errs);
      run("*", c, errs);
    },
    _context: ctx
  };
  Object.defineProperty(CT, "context", { enumerable: true, get: function () { return Object.assign({}, ctx); } });
  window.CustomTracking = CT;
  window.CustomTracking._probeParentAccess = function () {
    try {
      var d = window.parent && window.parent.document;
      return { attempted: true, gotDocument: !!d };
    } catch (e) {
      return { attempted: true, error: String(e && e.name) };
    }
  };
  window.addEventListener("message", function (ev) {
    var data = ev && ev.data;
    if (!data || data.type !== "lf-tracking-event" || ev.source !== P) return;
    if (data.context) { ctx = data.context; CT._context = ctx; }
    var errs = [];
    var n = run(data.event, data.context, errs);
    if (ALIAS[data.event]) n += run(ALIAS[data.event], data.context, errs);
    n += run("*", data.context, errs);
    report({ kind: "event", event: data.event, listeners: n, errors: errs, context: data.context });
    try { P.postMessage({ type: "lf-tracking-heard", event: data.event, listeners: n, errors: errs.length, nonce: NONCE }, "*"); } catch (e) {}
  });
  if (TEST) {
    var calls = 0;
    var call = function (via, url, method) {
      if (calls++ < 50) report({ kind: "call", via: via, url: String(url || "").slice(0, 500), method: method || "GET" });
    };
    window.fetch = function (input, init) {
      call("fetch", (input && input.url) || input, init && init.method);
      return Promise.resolve(new Response(null, { status: 204 }));
    };
    var xhrOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (m, u) { this.__lf = [m, u]; return xhrOpen.apply(this, arguments); };
    XMLHttpRequest.prototype.send = function () { call("xhr", this.__lf && this.__lf[1], this.__lf && this.__lf[0]); };
    if (navigator.sendBeacon) navigator.sendBeacon = function (u) { call("beacon", u, "POST"); return true; };
    Object.defineProperty(HTMLImageElement.prototype, "src", {
      configurable: true,
      get: function () { return this.__lfsrc || ""; },
      set: function (v) { this.__lfsrc = v; call("image", v, "GET"); }
    });
    window.addEventListener("error", function (e) {
      report({ kind: "error", message: String((e && e.message) || "Script error"), source: (e && e.filename) || "" });
    });
    window.addEventListener("load", function () {
      report({ kind: "ready", context: Object.assign({}, ctx), listening: Object.keys(listeners).filter(function (k) { return listeners[k].length; }) });
    });
  }
  try { var me = document.currentScript; if (me && me.parentNode) me.parentNode.removeChild(me); } catch (e) {}
})();
</script>
</head><body>__SNIPPET__</body></html>`;
  const ctx = { ...(context || {}) };
  delete ctx.event;
  const ctxJson = JSON.stringify(ctx).replace(/</g, "\\u003c").replace(/\u2028/g, "\\u2028").replace(/\u2029/g, "\\u2029");
  const snippetHtml = String(snip.code_snippet || "").replace(/<\/html>/gi, "<\\/html>");
  return wrapper
    .replace("__TEST__", () => (test ? "true" : "false"))
    .replace("__NONCE__", () => JSON.stringify(String(nonce || "")))
    .replace("__CONTEXT__", () => ctxJson)
    .replace("__SNIPPET__", () => snippetHtml);
}
